import * as React from 'react';

import { IConfig } from './types/Config.Type';
import { ILoaderProps } from './map/layer/ILoaderProps';

import { Ports } from './map/layers/kilimo/points/Ports';
import { NavigationRoutes } from './map/layers/kilimo/lines/NavigationRoutes';
import { FloatingSiteSelection } from './map/layers/kilimo/suitability/FloatingSiteSelection';
import { CocultureSiteSelection } from './map/layers/kilimo/suitability/CocultureSiteSelection';
import { StakedSiteSelection } from './map/layers/kilimo/suitability/StakedSiteSelection';
import { SeaweedFarms } from './map/layers/kilimo/polygons/SeaweedFarms';
import { FloatingSiteSelectionIslands } from './map/layers/kilimo/suitability/FloatingSiteSelectionIslands';
import { StakedSiteSelectionIslands } from './map/layers/kilimo/suitability/StakedSiteSelectionIslands';
import { CocultureSiteSelectionIslands } from './map/layers/kilimo/suitability/CocultureSiteSelectionIslands';
import { WaterQuality } from './map/layers/kilimo/suitability/WaterQuality';
import { Socioeconomic } from './map/layers/kilimo/suitability/Socioeconomic';
import { Hydrodynamics } from './map/layers/kilimo/suitability/Hydrodynamics';

const Config: IConfig = {
  map: {
    mapKey: "pk.eyJ1IjoibG9uZ2xpbmVlbnZpcm9ubWVudCIsImEiOiJjbGF0cHF1ZWUwM2l0M3FwcDcyN3B1YXpmIn0.snFi9yTPEZ5lfQxE3h3Epg",
    greyStyle: "mapbox://styles/longlineenvironment/clatpsjsl003r15okdwsdclmi",
    satelliteStyle: "mapbox://styles/longlineenvironment/clnkins3a001s01qw9k3efaj5/draft",
    longitude: 39.36, 
    latitude: -7.15,  
    zoom: 6, 
    minZoom: 5,
    maxZoom: 18,
    bearing: 0,
    pitch: 30,
    max_bounds: [
      [19, -27],   // Southwest (lng/lat)
      [59, 13]     // Northeast (lng/lat)
    ],
  },
  UI: {
    height: 75,
    before_layer: 'waterway-label',
    large_screen: 900,
    load_delay_base: 0,
    load_delay_random: 0,
  },

  suitability: [
    { name: 'Spatial constraints', color: '#aaaaaa', hidden: true },
    { name: 'Not suitable', color: '#dc1010' },
    { name: 'Moderately suitable', color: '#ff6d01' },
    { name: 'Suitable', color: '#128800' },
    { name: 'Highly suitable', color: '#0cf600' },
  ],  

  datasets: [
    {
      key: 'floating_site_selection',
      ids: [ 'floating_site_selection' ],
      title: 'Floating longline / raft mariculture',
      description: 'Anchored floating line / raft seaweed mariculture',
      active: false,
      group: 'atlas',
      renderer: (p: ILoaderProps) => <FloatingSiteSelection {...p}/>
    },       
    {
      key: 'staked_site_selection',
      ids: [ 'staked_site_selection' ],
      title: 'Staked line mariculture',
      description: 'Staked off-bottom seaweed mariculture',
      active: false,
      group: 'atlas',
      renderer: (p: ILoaderProps) => <StakedSiteSelection {...p}/>
    },  
    {
      key: 'coculture_site_selection',
      ids: [ 'coculture_site_selection' ],
      title: ' Seaweed / sandfish co-culture',
      description: 'Integrated Multitrophic Aquaculture using seaweed and sandfish',
      active: false,
      group: 'atlas',
      renderer: (p: ILoaderProps) => <CocultureSiteSelection {...p}/>
    },    
    /* {
      key: 'floating_site_selection_islands',
      ids: [ 'floating_site_selection_islands' ],
      title: 'Longline seaweed aquaculture in Pemba and Zanzibar',
      description: 'Longline culture suitability in Pemba and Zanzibar',
      active: false,
      group: 'spatial_planning',
      renderer: (p: ILoaderProps) => <FloatingSiteSelectionIslands {...p}/>
    },            
    {
      key: 'staked_site_selection_islands',
      ids: [ 'staked_site_selection_islands' ],
      title: 'Fixed bottom seaweed aquaculture in Pemba and Zanzibar',
      description: 'Fixed bottom culture suitability in Pemba and Zanzibar',
      active: false,
      group: 'spatial_planning',
      renderer: (p: ILoaderProps) => <StakedSiteSelectionIslands {...p}/>
    },    
    {
      key: 'coculture_site_selection_islands',
      ids: [ 'coculture_site_selection_islands' ],
      title: 'Combined seaweed aquaculture in Pemba and Zanzibar',
      description: 'Fixed bottom/longline/raft suitability in Pemba and Zanzibar',
      active: false,
      group: 'atlas',
      renderer: (p: ILoaderProps) => <CocultureSiteSelectionIslands {...p}/>
    }, */      
    {
      key: 'water_quality',
      ids: [ 'water_quality' ],
      title: 'Water quality suitability',
      description: 'Water quality suitability for seaweed farming',
      active: false,
      group: 'spatial_planning',
      renderer: (p: ILoaderProps) => <WaterQuality {...p}/>
    },      
    {
      key: 'socioeconomic',
      ids: [ 'socioeconomic' ],
      title: 'Socioeconomic suitability',
      description: 'Socioeconomic suitability for seaweed farming',
      active: false,
      group: 'spatial_planning',
      renderer: (p: ILoaderProps) => <Socioeconomic {...p}/>
    },   
    {
      key: 'hydrodynamics',
      ids: [ 'hydrodynamics' ],
      title: 'Hydrodynamics suitability',
      description: 'Hydrodynamics suitability for seaweed farming',
      active: false,
      group: 'spatial_planning',
      renderer: (p: ILoaderProps) => <Hydrodynamics {...p}/>
    },          
    {
      key: 'seaweed_farms',
      ids: [ 'seaweed_farms_polygons' ],
      title: 'Aquaculture registry',
      description: 'Aquaculture farming registry',
      active: true,
      group: 'registry',
      renderer: (p: ILoaderProps) => <SeaweedFarms {...p}/>
    },         
    {
      key: 'ports',
      ids: [ 'ports' ],
      title: 'Ports',
      description: 'Ferry boat ports in Tanzania',
      group: 'infrastructure',
      active: false,
      renderer: (p: ILoaderProps) => <Ports {...p}/>
    },  
    {
      key: 'navigation_routes',
      ids: [ 'navigation_routes' ],
      title: 'Ferry routes',
      description: 'Ferry routes in Tanzania',
      group: 'infrastructure',
      active: false,
      renderer: (p: ILoaderProps) => <NavigationRoutes {...p}/>
    },       
  ],
  datagroups: [
    {
      key: 'atlas',
      title: 'Kilimo Blue',
      description: 'Tanzania aquaculture atlas',
      exclusive: true,      
    },
    {
      key: 'registry',
      title: 'Aquaculture registry',
      description: 'Aquaculture farming registry',
      exclusive: false,      
    },       
    {
      key: 'spatial_planning',
      title: 'Spatial Planning',
      description: 'Aquaculture suitability layers',
      exclusive: false,      
    },
    {
      key: 'infrastructure',
      title: 'Infrastructure',
      description: 'Tanzania infrastructure access',
      exclusive: false,      
    }
  ],
  dictionary: [    
    {
      key: 'suitability',
      text: 'Suitability',
      type: 'suitability'
    }, {
      key: 'area_km2',
      text: 'Area',
      unit: <>km&sup2;</>,
      type: 'number'
    }, {
      key: 'ADM1_EN',
      text: 'Province'
    }, {
      key: 'ADM2_EN',
      text: 'District'
    }, {
      key: 'ADM3_EN',
      text: 'Ward'
    }, {
      key: 'type',
      text: 'Type'
    }, {
      key: 'name',
      text: 'Name'
    }, {
      key: 'culture_type',
      text: 'Culture type'
    }, {
      key: 'structure_type',
      text: 'Structure type'
    }, {
      key: 'farm_name',
      text: 'Farm name'
    }, {
      key: 'owner',
      text: 'Owner'
    }, {
      key: 'use',
      text: 'Use'
    }, {
      key: 'species',
      text: 'Species'
    }, {
      key: 'company',
      text: 'Company'
    }, {
      key: 'medium',
      text: 'Medium'
    }, {
      key: 'location',
      text: 'Location'
    }, {
      key: 'setup',
      text: 'Setup'
    }, {
      key: 'area_m2',
      text: 'Area',
      unit: <>m&sup2;</>,
      type: 'number',
      decimals: 0
    }, {
      key: 'vol_m3',
      text: 'Volume',
      unit: <>m&sup3;</>,
      type: 'number',
      decimals: 0
    }, {
      key: 'island',
      text: 'Island'
    }, {
      key: 'floating',
      text: 'Longline suitability',
      type: 'suitability'
    }, {
      key: 'staked',
      text: 'Fixed bottom suitability',
      type: 'suitability'
    }, {
      key: 'coculture',
      text: 'Combined suitability',
      type: 'suitability'
    }, 
    
    
  ]
}

export { Config }
